import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, Check } from "lucide-react";
import { api } from "@/lib/api";

interface Notification {
  id: string;
  type: string;
  payloadJsonb: Record<string, unknown>;
  readAt: string | null;
  createdAt: string;
}

/**
 * T150 — header bell with unread badge + dropdown listing recent
 * notifications (from /api/v1/notifications, populated by the cert-expiry
 * cron + future US6 events). Mark-as-read on click; "Mark all read" at
 * the bottom.
 */
export function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ["notifications"],
    queryFn: () => api.get<{ notifications: Notification[]; unread: number }>("/notifications?limit=20"),
    refetchInterval: 60_000,
    retry: false,
  });

  const markRead = useMutation({
    mutationFn: (id: string) => api.post(`/notifications/${id}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
  const markAllRead = useMutation({
    mutationFn: () => api.post(`/notifications/read-all`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const unread = q.data?.unread ?? 0;
  const items = q.data?.notifications ?? [];

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label={`Notifications (${unread} unread)`}
        className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-subtle relative"
      >
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-danger text-white text-[10px] font-mono inline-flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div className="fixed left-4 right-4 top-14 sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-1 sm:w-80 z-50 of-card max-h-[60vh] overflow-auto shadow-of-lg">
            <div className="of-card-header sticky top-0 bg-background">
              <h3 className="text-sm font-semibold">Notifications</h3>
              {unread > 0 && (
                <button
                  onClick={() => markAllRead.mutate()}
                  className="text-2xs text-accent hover:underline"
                >
                  Mark all read
                </button>
              )}
            </div>
            {items.length === 0 && (
              <div className="px-4 py-10 text-center text-xs text-muted-foreground">
                No notifications.
              </div>
            )}
            {items.map((n) => (
              <button
                key={n.id}
                onClick={() => {
                  if (!n.readAt) markRead.mutate(n.id);
                }}
                className={`w-full text-left px-3 py-2.5 border-b border-border-muted last:border-b-0 hover:bg-subtle/60 transition-colors ${
                  !n.readAt ? "bg-accent-subtle/40" : ""
                }`}
              >
                <div className="flex items-start gap-2">
                  {!n.readAt && <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-accent shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold">
                      {renderTitle(n)}
                    </div>
                    <div className="text-2xs text-muted-foreground mt-0.5">
                      {renderBody(n)}
                    </div>
                    <div className="text-2xs text-subtle-foreground mt-0.5 font-mono">
                      {relative(n.createdAt)}
                    </div>
                  </div>
                  {n.readAt && <Check className="h-3 w-3 text-subtle-foreground" />}
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function renderTitle(n: Notification): string {
  if (n.type === "wildcard.expiring") return "Wildcard cert expiring";
  return n.type;
}

function renderBody(n: Notification): string {
  if (n.type === "wildcard.expiring") {
    const { apex, daysToExpiry } = n.payloadJsonb as { apex: string; daysToExpiry: number };
    return `${apex} expires in ${daysToExpiry} day${daysToExpiry === 1 ? "" : "s"}.`;
  }
  return JSON.stringify(n.payloadJsonb);
}

function relative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}
